import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { FormState } from 'formstate';

import { InputFieldState, setFormStateValues, commitFormState } from '@servicetitan/form-state';

import { inject, injectable } from '@servicetitan/react-ioc';

import { ConfigurationManagementApi, SmsConfiguration } from '../api/configuration-management.api';
import { LoadStatus } from '../../common/enums/load-status';
import { ManageTenantStore } from './manage-tenant.store';
import { toNumberOrNull } from '../utils/form-value-helpers';

@injectable()
export class TenantSmsConfigurationStore {
    @observable smsConfiguration?: SmsConfiguration;
    @observable loadStatus = LoadStatus.None;
    @observable saveLoadStatus = LoadStatus.None;

    formState = new FormState({
        marketingTcrCampaignCallToActionMessageId: new InputFieldState<number | null>(null),
        marketingTcrCampaignHelpMessageId: new InputFieldState<number | null>(null),
        marketingTcrCampaignMessageFlowId: new InputFieldState<number | null>(null),
        marketingTcrCampaignOptInConfirmationMessageId: new InputFieldState<number | null>(null),
        marketingTcrCampaignOptInMessageId: new InputFieldState<number | null>(null),
        marketingTcrCampaignOptOutMessageId: new InputFieldState<number | null>(null),
        customMarketingSmsOptInMessage: new InputFieldState(''),
    });

    private hasInitialized = false;

    @computed get tenant() {
        return this.manageTenantStore.tenant;
    }

    @computed get isFormChanged() {
        return (
            this.formState.$.marketingTcrCampaignCallToActionMessageId.dirty ||
            this.formState.$.marketingTcrCampaignHelpMessageId.dirty ||
            this.formState.$.marketingTcrCampaignMessageFlowId.dirty ||
            this.formState.$.marketingTcrCampaignOptInConfirmationMessageId.dirty ||
            this.formState.$.marketingTcrCampaignOptInMessageId.dirty ||
            this.formState.$.marketingTcrCampaignOptOutMessageId.dirty ||
            this.formState.$.customMarketingSmsOptInMessage.dirty
        );
    }

    constructor(
        @inject(ConfigurationManagementApi)
        private readonly configurationManagementApi: ConfigurationManagementApi,
        @inject(ManageTenantStore)
        private readonly manageTenantStore: ManageTenantStore,
    ) {
        makeObservable(this);
    }

    init = async () => {
        // Only fetch once, not on every tab switch
        if (this.hasInitialized) {
            return;
        }

        this.hasInitialized = true;
        await this.fetchSmsConfiguration();
    };

    @action fetchSmsConfiguration = async () => {
        runInAction(() => {
            this.loadStatus = LoadStatus.Loading;
        });

        try {
            const { data } = await this.configurationManagementApi.tenantSmsConfigurationGET(
                this.tenant?.internalId,
                this.tenant?.partnerId,
            );

            runInAction(() => {
                this.smsConfiguration = data;
                setFormStateValues(this.formState, {
                    marketingTcrCampaignCallToActionMessageId:
                        data.marketingTcrCampaignCallToActionMessageId,
                    marketingTcrCampaignHelpMessageId: data.marketingTcrCampaignHelpMessageId,
                    marketingTcrCampaignMessageFlowId: data.marketingTcrCampaignMessageFlowId,
                    marketingTcrCampaignOptInConfirmationMessageId:
                        data.marketingTcrCampaignOptInConfirmationMessageId,
                    marketingTcrCampaignOptInMessageId: data.marketingTcrCampaignOptInMessageId,
                    marketingTcrCampaignOptOutMessageId: data.marketingTcrCampaignOptOutMessageId,
                    customMarketingSmsOptInMessage: data.customMarketingSmsOptInMessage ?? '',
                });
                commitFormState(this.formState);
                this.loadStatus = LoadStatus.Ok;
            });
        } catch (error) {
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });
        }
    };

    @action save = async () => {
        if (!this.tenant) {
            return;
        }
        runInAction(() => (this.saveLoadStatus = LoadStatus.Loading));

        try {
            const fields = this.formState.$;

            const payload = {
                marketingTcrCampaignCallToActionMessageId: toNumberOrNull(
                    fields.marketingTcrCampaignCallToActionMessageId.value,
                ),
                marketingTcrCampaignHelpMessageId: toNumberOrNull(
                    fields.marketingTcrCampaignHelpMessageId.value,
                ),
                marketingTcrCampaignMessageFlowId: toNumberOrNull(
                    fields.marketingTcrCampaignMessageFlowId.value,
                ),
                marketingTcrCampaignOptInConfirmationMessageId: toNumberOrNull(
                    fields.marketingTcrCampaignOptInConfirmationMessageId.value,
                ),
                marketingTcrCampaignOptInMessageId: toNumberOrNull(
                    fields.marketingTcrCampaignOptInMessageId.value,
                ),
                marketingTcrCampaignOptOutMessageId: toNumberOrNull(
                    fields.marketingTcrCampaignOptOutMessageId.value,
                ),
                customMarketingSmsOptInMessage: fields.customMarketingSmsOptInMessage.value,
            } as SmsConfiguration;

            await this.configurationManagementApi.tenantSmsConfigurationPOST(
                this.tenant?.internalId,
                this.tenant?.partnerId,
                payload,
            );

            runInAction(() => {
                commitFormState(this.formState);
                this.saveLoadStatus = LoadStatus.Ok;
            });
        } catch (e) {
            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Error;
            });
        }
    };

    @action resetConfiguration = () => {
        this.formState.reset();
        this.saveLoadStatus = LoadStatus.None;
    };

    @action reset = () => {
        this.smsConfiguration = undefined;
        this.loadStatus = LoadStatus.None;
        this.hasInitialized = false;
        this.formState.reset();
        this.saveLoadStatus = LoadStatus.None;
    };
}
